<template>
  <Alert
    v-if="success"
    class="mt-4"
    type="success"
    data-i18n="newsletterSuccess"
  >
    {{ t('newsletterSuccess') }}
  </Alert>
  <form
    v-else
    class="flex flex-col w-full"
    @submit.prevent="registerEmail"
  >
    <Alert
      v-if="error"
      class="mt-4"
      type="error"
    >
      {{ error }}
    </Alert>
    <div class="flex flex-col">
      <div
        v-if="useAirports"
        class="w-full form-group"
      >
        <b-skeleton
          v-if="loading"
          height="50px"
        />
        <template v-else>
          <label
            class="mr-4"
            for="airport"
            data-i18n="newsletterPopup.airport"
          >{{ t('newsletterPopup.airport') }}</label>
          <select
            id="airport"
            v-model="airport"
            class="form-control"
          >
            <option
              v-for="ap in newsletterAirports"
              :key="ap.id"
              :value="ap.id"
            >
              {{ ap.name }}
            </option>
          </select>
        </template>
      </div>
      <div class="w-full form-group">
        <label
          v-if="$isSol"
          for="subscribe-email"
          data-i18n="newsletterPopup.email"
        >{{ t('newsletterPopup.email') }}</label>
        <div class="flex flex-col md:flex-row text-center">
          <b-skeleton
            v-if="loading"
            width="75%"
            height="50px"
          />
          <template v-else>
            <input
              id="subscribe-email"
              v-model="email"
              class="form-control"
              type="email"
              autocomplete="email"
              :class="{
                'newsletter-form__input-bordered h-10': !$isSol,
                '!w-full md:max-w-[400px]': horizontal
              }"
            />
          </template>
          <b-skeleton
            v-if="loading"
            class="ml-4"
            width="20%"
            height="50px"
          />
          <template v-else>
            <button
              class="btn btn-bold mt-1 md:mt-0 md:ml-4 newsletter-form-register-btn rounded-full flex items-center justify-center w-fit"
              type="submit"
              :class="[
                $isSol
                  ? 'btn-orange btn-lg mt-4'
                  : 'btn-secondary btn-md font-montserrat font-semibold capitalize flex items-center',
                { 'max-lg:w-full': horizontal }
              ]"
              data-i18n="newsletterSubscribeButton"
            >
              <span
                v-if="!$isSol"
                class="icon mr-2 leading-none"
              >
                <fa icon="fa-envelope" />
              </span>{{ t('newsletterSubscribeButton') }}
            </button>
          </template>
        </div>
      </div>
    </div>
  </form>
</template>

<script setup>
import Alert from '@layers/web/components/alerts/Alert'

import localStorageKeys from '@layers/web/constants/local-storage-keys.ts'

const localeStore = useLocaleStore()
const rootStore = useRootStore()
const { t } = useI18n()
const { trackNewsletterSignup } = useTracking()

const props = defineProps({
  horizontal: {
    type: Boolean,
    default: false,
  },

  campaign: {
    type: String,
    default: undefined,
  },
})

const email = ref('')
const airport = ref(undefined)
const loading = ref(false)
const error = ref(false)
const success = ref(false)

const { newsletterAirports } = storeToRefs(rootStore)
const { locale } = storeToRefs(localeStore)

const useAirports = computed(() => {
  return isSol() && (newsletterAirports.value || []).length > 0
})

onMounted(() => {
  if (isSol()) {
    expand()
  }
})

const expand = (async () => {
  loading.value = true

  if (!newsletterAirports.value?.length) {
    await rootStore.fetchNewsletterLists()
  }

  airport.value = newsletterAirports.value?.[0]?.id

  loading.value = false
})

const registerEmail = (async () => {
  error.value = false

  if (!email.value) {
    error.value = t('formFieldRequiredError')
    return
  }

  // Gtag event
  trackNewsletterSignup(airport.value)

  try {
    const { data: result, error } = await apiFetch(`/${locale.value}/newsletter-register`, {
      method: 'POST',
      body: {
        email: email.value,
        airpiort: airport.value, // yes, it's a typo in the endpoint
        campaign: props.campaign,
      },
    })
    if (error) {
      error.value = error.message

      return false
    }

    if (result) {
      try {
        window.localStorage.setItem(localStorageKeys.hideNewsletter, true)
      } catch (e) {
        // ignore
      }

      success.value = true
    }
  } catch (error) {
    error.value = error.data?.message || t('serverError')
  }
})
</script>

<style lang="scss" scoped>
.newsletter-form {
  &__input-bordered {
    border-radius: 8px;
    border: 2px solid theme('colors.medium-blue');
    width: 320px;
  }
}
</style>
